// Lightweight Google Analytics 4 wrapper.
// GA only loads on production hostnames (see index.html). On staging/preview/localhost
// the gtag function is absent, so calls here are safe everywhere.
//
// IMPORTANT: this wrapper does NOT gate on consent. GA4 runs in Consent Mode v2
// (defaults set in index.html). When analytics_storage is "denied", gtag.js
// itself converts our hits into cookieless pings — Google models them server-side.
// Gating the wrapper would silently drop the vast majority of SPA pageviews from
// non-consenting users instead of letting Consent Mode do its job.
// Consent banner behavior is unchanged and lives in src/lib/consent.ts.

const GA_MEASUREMENT_ID = "G-MNY6GNSCBT";
const PRODUCTION_HOSTS = new Set(["authoritylayer.app", "www.authoritylayer.app"]);

export function isAnalyticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return PRODUCTION_HOSTS.has(window.location.hostname);
}

type GtagFn = (...args: unknown[]) => void;

function gtag(): GtagFn | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { gtag?: GtagFn };
  return typeof w.gtag === "function" ? w.gtag : null;
}

export function trackPageView(path: string, title?: string): void {
  if (!isAnalyticsEnabled()) return;
  const fn = gtag();
  if (!fn) return;
  fn("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled()) return;
  const fn = gtag();
  if (!fn) return;
  fn("event", name, { ...(params ?? {}), send_to: GA_MEASUREMENT_ID });
}

